import React from "react";
import { Box, Typography, IconButton, Tooltip, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Buyer } from "../../../../Models/Buyer";
import { Seller } from "../../../../Models/Seller";
import "./AdminUserCard.scss";

interface AdminUserCardProps {
  user: Buyer | Seller;
  userType: "buyer" | "seller";
  onEdit: () => void;
  onCopy: (text: string, fieldName: string) => void;
}

const AdminUserCard: React.FC<AdminUserCardProps> = ({ user, userType, onEdit, onCopy }) => {
  const isBuyer = userType === "buyer";
  const buyer = isBuyer ? (user as Buyer) : null;
  const seller = !isBuyer ? (user as Seller) : null;

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Box className={`admin-user-card ${isBuyer ? 'buyer-card' : 'seller-card'}`}>
      <Box className="card-header">
        <Box className="user-info">
          {user.profileImage && (
            <img src={user.profileImage} alt={user.fullName} className="user-avatar" />
          )}
          <Box>
            <Typography variant="h6" className="user-name">
              {user.fullName}
            </Typography>
            {/* Show seller tag only if buyer is also a seller */}
            {isBuyer && buyer?.seller && (
              <Chip
                label="Seller"
                size="small"
                color="secondary"
                className="user-type-chip"
              />
            )}
          </Box>
        </Box>
        <Box className="user-actions">
          <Tooltip title="View Details & Edit">
            <IconButton onClick={onEdit} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className="card-body">
        {isBuyer && buyer && (
          <>
            <Typography variant="body2" className="info-line">
              <span className="info-key">Buyer ID:</span> {user.id}
              <IconButton
                size="small"
                onClick={() => onCopy(user.id, "Buyer ID")}
                className="copy-btn-inline"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Email:</span> {user.email}
              <IconButton
                size="small"
                onClick={() => onCopy(user.email, "Email")}
                className="copy-btn-inline"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Typography>

            {buyer.seller && (
              <Typography variant="body2" className="info-line">
                <span className="info-key">Seller ID:</span> {buyer.seller}
                <IconButton
                  size="small"
                  onClick={() => onCopy(buyer.seller!, "Seller ID")}
                  className="copy-btn-inline"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Typography>
            )}

            <Typography variant="body2" className="info-line">
              <span className="info-key">Mobile Number:</span> {buyer.mobileNo || 'Not provided'}
              {buyer.mobileNoVerified && (
                <Chip label="✓" size="small" color="success" className="verified-chip-inline" />
              )}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Mobile Verified:</span> {buyer.mobileNoVerified ? 'Yes' : 'No'}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Profile Image:</span> {buyer.profileImage ? 'Yes' : 'No'}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">About:</span> {buyer.about ? (buyer.about.length > 50 ? buyer.about.substring(0, 50) + '...' : buyer.about) : 'Not provided'}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Registered On:</span> {formatDate(buyer.createdAt)}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Chat Token:</span> 
              <span style={{ color: '#94a3b8' }}>••••••••</span>
              <Tooltip title="Copy chat token">
                <IconButton
                  size="small"
                  onClick={() => onCopy(buyer.chatToken, "Chat Token")}
                  className="copy-btn-inline"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>

          </>
        )}

        {!isBuyer && seller && (
          <>
            <Typography variant="body2" className="info-line">
              <span className="info-key">Seller ID:</span> {user.id}
              <IconButton
                size="small"
                onClick={() => onCopy(user.id, "Seller ID")}
                className="copy-btn-inline"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Email:</span> {user.email}
              <IconButton
                size="small"
                onClick={() => onCopy(user.email, "Email")}
                className="copy-btn-inline"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Contact Number:</span> {seller.contactNumber || 'Not provided'}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Gender:</span> {seller.gender || 'Not specified'}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Date of Birth:</span> {formatDate(seller.dateOfBirth)}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Occupation:</span> {seller.occupation || "N/A"}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Tagline:</span> {seller.tagline ? (seller.tagline.length > 50 ? seller.tagline.substring(0, 50) + '...' : seller.tagline) : 'N/A'}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Profile Image:</span> {seller.profileImage ? 'Yes' : 'No'}
            </Typography>

            {seller.location && (
              <Typography variant="body2" className="info-line">
                <span className="info-key">Location:</span> {seller.location.state}, {seller.location.country}
              </Typography>
            )}

            <Typography variant="body2" className="info-line">
              <span className="info-key">User Verified:</span> {seller.userVerified ? 'Yes' : 'No'}
              {seller.userVerified && (
                <Chip label="✓" size="small" color="success" className="verified-chip-inline" />
              )}
            </Typography>

            <Typography variant="body2" className="info-line">
              <span className="info-key">Agreement Signed:</span> {seller.userAgreement ? 'Yes' : 'No'}
              {seller.userAgreement && (
                <Chip label="✓" size="small" color="info" className="verified-chip-inline" />
              )}
            </Typography>

            {seller.buyer && (
              <Typography variant="body2" className="info-line">
                <span className="info-key">Buyer ID:</span> {seller.buyer}
                <IconButton
                  size="small"
                  onClick={() => onCopy(seller.buyer, "Buyer ID")}
                  className="copy-btn-inline"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Typography>
            )}

            {/* Plan Details Section */}
            <Box sx={{ 
              mt: 2, 
              p: 1.5, 
              background: seller.planDetails 
                ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: 1,
              border: seller.planDetails ? '2px solid #3b82f6' : '2px solid #cbd5e1'
            }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 700, 
                color: seller.planDetails ? '#1e40af' : '#64748b', 
                display: 'block', 
                mb: 1 
              }}>
                💼 {seller.planDetails ? 'Active Plan' : 'No Active Plan'}
              </Typography>
              {seller.planDetails ? (
                <>
                  <Typography variant="body2" className="info-line" sx={{ mb: 0.5 }}>
                    <span className="info-key">Plan ID:</span> {typeof seller.planDetails.plan === 'string' ? seller.planDetails.plan : seller.planDetails.plan._id || 'N/A'}
                  </Typography>
                  <Typography variant="body2" className="info-line" sx={{ mb: 0.5 }}>
                    <span className="info-key">Expires:</span> {formatDate(seller.planDetails.expiryTimeStamp.toString())}
                    {new Date(seller.planDetails.expiryTimeStamp) < new Date() && (
                      <Chip label="Expired" size="small" color="error" sx={{ ml: 1, height: '20px' }} />
                    )}
                    {new Date(seller.planDetails.expiryTimeStamp) >= new Date() && (
                      <Chip label="Active" size="small" color="success" sx={{ ml: 1, height: '20px' }} />
                    )}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                  Using default platform fees
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminUserCard;
